import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, ShieldCheck, ChartLine, Globe, Lightning, Compass, MapPin, Crosshair, Lock, Download, Heart, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { getPublicStats } from "../lib/publicStats";
import BusinessCard from "../components/BusinessCard";
import AutocompleteSearch from "../components/AutocompleteSearch";
import SocialTicker from "../components/SocialTicker";
import MostSharedWidget from "../components/MostSharedWidget";
import { OpenGuideLink } from "../components/OnboardingGuide";
import SeoHelmet from "../components/SeoHelmet";

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Comment trouver un commerce dans le Haut-Rhin (68) ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Mon Commerce 68 référence plus de 115 000 commerces, artisans et prestataires de Mulhouse, Colmar, Saint-Louis, Guebwiller, Thann et toutes les communes du département 68. Recherche par catégorie, ville, mots-clés ou via notre Concierge IA en français, anglais ou allemand."
      }
    },
    {
      "@type": "Question",
      "name": "Comment ajouter mon entreprise sur l'annuaire ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Toutes les entreprises légalement enregistrées en Haut-Rhin (INSEE) sont déjà présentes. Pour réclamer votre fiche gratuitement, créez un compte sur moncommerce68.fr puis cliquez « Réclamer ma fiche ». Activez ensuite l'essai Premium 14 jours sans carte bancaire pour ajouter photos, horaires et descriptions."
      }
    },
    {
      "@type": "Question",
      "name": "Mon Commerce 68 est-il gratuit pour les commerçants ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, la fiche basique est 100 % gratuite et le restera. Pour activer les options avancées (photos illimitées, contacts directs, statistiques, IA, flyer PDF), passez Premium à partir de 7,50€/mois la 1re année."
      }
    },
    {
      "@type": "Question",
      "name": "Le site est-il accessible aux frontaliers suisses et allemands ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, l'interface est disponible en français, anglais et allemand. Idéal pour les frontaliers de Bâle, Saint-Louis, Mulhouse et Colmar qui cherchent commerces, artisans et services en Alsace du Sud."
      }
    },
    {
      "@type": "Question",
      "name": "Quelles villes sont couvertes par Mon Commerce 68 ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Toutes les communes du Haut-Rhin (département 68) : Mulhouse, Colmar, Saint-Louis, Guebwiller, Thann, Wittenheim, Illzach, Riedisheim, Cernay, Altkirch, Ribeauvillé, Munster, Kingersheim, Pfastatt, Kaysersberg, Eguisheim, Rouffach, Soultz, et 380+ autres."
      }
    }
  ]
};

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e9b7ae2f-912a-44be-b8e6-1671b18272f6/images/a91d767768dab17a17bc2ce6fd45e85535e15222b1d40d46198476b20f19ab67.png";

// A/B/C test variants — H1 only applies to FR (primary market). EN/DE keep their original i18n strings.
const HERO_VARIANTS = {
  a: {
    h1: "Votre commerce mérite d'être trouvé par tous les habitants du Haut-Rhin.",
    sub: "Mon Commerce 68 met votre fiche devant les Mulhousiens, Colmariens et frontaliers qui cherchent un commerce comme le vôtre — 14 jours gratuits, sans carte bancaire.",
  },
  b: {
    h1: "Plus d'appels. Plus de clients. Sans bouger de votre comptoir.",
    sub: "115 000 commerces du 68 référencés. Le vôtre peut passer en tête en 5 minutes — et tester Premium 14 jours gratuits.",
  },
  c: {
    h1: "Vos clients vous cherchent en ligne. Apparaissez avant les autres.",
    sub: "L'annuaire premium du Haut-Rhin, pensé pour boulangers, plombiers, coiffeurs et tous les indépendants locaux. 14 jours gratuits, aucune carte demandée.",
  },
};

function pickHeroVariant() {
  try {
    const cached = sessionStorage.getItem("mc68_hero_variant");
    if (cached && HERO_VARIANTS[cached]) return cached;
    const v = ["a", "b", "c"][Math.floor(Math.random() * 3)];
    sessionStorage.setItem("mc68_hero_variant", v);
    return v;
  } catch {
    return "a";
  }
}

export default function Home() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [heroVariant] = useState(() => (lang === "fr" ? pickHeroVariant() : null));

  useEffect(() => {
    getPublicStats().then((r) => setStats(r.data)).catch(() => {});
    // Live refresh — every 60s (server + client cache 60s), fetch /stats again so the "Recherches effectuées"
    // counter ticks up in real time when other visitors search. Hard cap on
    // bandwidth: only this one tiny endpoint, paused when tab is hidden.
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      getPublicStats().then((r) => setStats(r.data)).catch(() => {});
    };
    const iv = setInterval(tick, 60000);
    api.get("/public/businesses", { params: { premium_only: true, limit: 6 } })
      .then((r) => {
        const items = r.data.items || [];
        if (items.length) { setFeatured(items); return null; }
        // Fallback: if no premium businesses, show latest
        return api.get("/public/businesses", { params: { limit: 6 } })
          .then((r2) => setFeatured(r2.data.items || []));
      })
      .catch(() => {});
    // Track hero variant impression once per session
    if (heroVariant) {
      try {
        const k = `mc68_hero_impr_${heroVariant}`;
        if (!sessionStorage.getItem(k)) {
          api.post("/public/track/hero-ab", { variant: heroVariant, action: "impression" }).catch(() => {});
          sessionStorage.setItem(k, "1");
        }
      } catch { /* noop */ }
    }
    return () => clearInterval(iv);
  }, [heroVariant]);

  const trackHeroCtaClick = () => {
    if (!heroVariant) return;
    api.post("/public/track/hero-ab", { variant: heroVariant, action: "cta_click" }).catch(() => {});
  };

  const heroCopy = heroVariant ? HERO_VARIANTS[heroVariant] : { h1: t.home.h1, sub: t.home.sub };

  return (
    <div data-testid="home-page">
      <SeoHelmet
        title="Mon Commerce 68 — Annuaire premium de 115 000 entreprises du Haut-Rhin (Mulhouse, Colmar)"
        description="Annuaire premium des commerces, artisans et prestataires du Haut-Rhin (68). Mulhouse, Colmar, Saint-Louis, Guebwiller, Thann + 380 villes. Recherche IA, avis vérifiés, contacts directs. Frontaliers suisses & allemands : EN/DE disponibles."
        path="/"
        ogImage="https://www.moncommerce68.fr/logo-mc68.png"
        jsonLd={[FAQ_SCHEMA]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="aurora" />
        <div className="absolute inset-0 -z-10 grid-fine" />
        <div className="absolute -z-10 right-0 top-0 w-[55%] h-full hidden lg:block">
          <img src={HERO_IMG} alt="Haut-Rhin" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent" />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 glass-pill rounded-full px-3 py-1.5 mb-6" data-testid="hero-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                <span className="label-uppercase !text-[0.65rem] !tracking-[0.2em]">{t.home.kicker}</span>
              </div>
              <h1 className="font-display text-[2.25rem] sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-foreground max-w-3xl" data-testid={heroVariant ? `hero-h1-variant-${heroVariant}` : "hero-h1"}>
                <span className="text-gradient">{heroCopy.h1}</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">{heroCopy.sub}</p>
              {typeof stats?.businesses === "number" && (
                <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="hero-live-pills">
                  <div className="inline-flex items-center gap-2 glass-pill rounded-full pl-2 pr-3 py-1" data-testid="hero-live-count">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
                    </span>
                    <span className="text-xs font-semibold tabular-nums">
                      <LiveHeroCount value={stats.businesses} />
                    </span>
                    <span className="text-xs text-muted-foreground">entreprises du 68 référencées</span>
                  </div>
                  {typeof stats?.searches === "number" && stats.searches > 100 && (
                    <LiveSearchesPill total={stats.searches} last7d={stats.searches_7d} />
                  )}
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-10 max-w-2xl"
              data-testid="hero-search-wrap"
            >
              <AutocompleteSearch placeholder={t.home.search_placeholder || "Tapez le nom d'une entreprise…"} fallbackPath="/recherche" />
              <div className="mt-3 flex items-center gap-3" data-testid="hero-search-form">
                <Link
                  to="/recherche"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-primary hover:text-secondary transition-colors group"
                  data-testid="hero-ai-cta"
                >
                  <Sparkle size={14} weight="fill" className="text-secondary group-hover:rotate-12 transition-transform" />
                  {t.home.smart_label || "Recherche IA"}
                  <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform"/>
                </Link>
                <span className="text-xs text-muted-foreground">· FR · EN · DE</span>
              </div>
            </motion.div>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/annuaire" className="group inline-flex items-center gap-2 text-sm font-semibold text-foreground border-b border-foreground pb-1 hover:text-primary hover:border-primary transition-colors" data-testid="cta-directory">
                {t.home.cta_directory} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/passez-partenaire" onClick={trackHeroCtaClick} className="group inline-flex items-center gap-2 text-sm font-semibold bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 rounded-sm transition-all hover:shadow-[0_12px_32px_-12px_rgba(200,121,90,0.5)]" data-testid="cta-partner">
                {t.home.cta_partner} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <NearbyButton navigate={navigate} />
            </div>
          </div>

          {/* Logo showcase — right column */}
          <div className="hidden lg:flex lg:col-span-5 items-start justify-end pr-0 -mt-16 xl:-mt-20 translate-x-4 xl:translate-x-8" data-testid="hero-logo-showcase">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute inset-0 -z-10 rounded-full bg-gradient-to-br from-primary/25 via-secondary/15 to-transparent blur-3xl scale-110" />
              <div className="absolute inset-0 -z-10 rounded-full ring-1 ring-primary/10 animate-pulse" />
              <img
                src="/logo-mc68.png"
                alt="Mon Commerce 68"
                className="w-[460px] h-[460px] xl:w-[540px] xl:h-[540px] object-contain drop-shadow-[0_24px_48px_rgba(19,78,52,0.28)]"
              />
            </motion.div>
          </div>
        </div>

        {/* Counters */}
        <div className="border-t border-border bg-accent/30 backdrop-blur-sm">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Counter label={t.home.proof_total} value={stats?.businesses ?? "—"} testid="counter-businesses" />
            <Counter label={t.home.proof_premium} value={stats?.premium_businesses ?? "—"} testid="counter-premium" />
            <Counter label={t.home.proof_cities} value={stats?.cities ?? "—"} testid="counter-cities" />
            <Counter label={t.home.proof_searches} value={stats?.searches ?? "—"} testid="counter-searches" />
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-uppercase mb-3">{t.home.featured_kicker}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">{t.home.featured_h2}</h2>
          </div>
          <Link to="/annuaire" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline" data-testid="see-all-link">
            {t.home.cta_directory} <ArrowRight size={14} />
          </Link>
        </div>

        {featured.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-sm p-12 text-center text-muted-foreground" data-testid="featured-empty">
            {t.directory.empty}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">
            {featured.slice(0, 6).map((b) => <BusinessCard key={b.id} b={b} />)}
          </div>
        )}
      </section>

      <MostSharedWidget />

      {/* Shopkeeper onboarding — simple 3-step flow for non-tech users */}
      <section className="border-t border-border bg-accent/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <div className="label-uppercase mb-3">{t.home.shopkeeper_kicker}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-tight mb-4">{t.home.shopkeeper_h2}</h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-xl leading-relaxed" data-testid="shopkeeper-lead">{(t.home.shopkeeper_lead || "").replace("{count}", (stats?.businesses ?? 21000).toLocaleString("fr-FR"))}</p>
              <form
                onSubmit={(e) => { e.preventDefault(); const v = e.target.elements.bizq.value.trim(); if (v) navigate(`/annuaire?q=${encodeURIComponent(v)}`); }}
                className="max-w-xl bg-card border-2 border-primary rounded-sm p-2 flex items-center gap-2 shadow-sm"
                data-testid="shopkeeper-search"
              >
                <input
                  name="bizq"
                  placeholder={t.home.shopkeeper_search_placeholder}
                  className="flex-1 min-w-0 px-3 py-3 bg-transparent text-base outline-none"
                  data-testid="shopkeeper-search-input"
                />
                <button type="submit" className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-3 rounded-sm font-semibold text-sm whitespace-nowrap" data-testid="shopkeeper-search-btn">
                  {t.home.shopkeeper_find}
                </button>
              </form>
            </div>
            <div className="lg:col-span-6 lg:pl-6">
              <div className="label-uppercase mb-4 text-secondary">{t.home.shopkeeper_steps_title}</div>
              <ol className="space-y-4">
                {[t.home.shopkeeper_step1, t.home.shopkeeper_step2, t.home.shopkeeper_step3].map((step, i) => (
                  <li key={`step-${i}`} className="flex items-start gap-4 bg-card border border-border rounded-sm p-4" data-testid={`shopkeeper-step-${i+1}`}>
                    <div className="w-8 h-8 shrink-0 bg-primary text-primary-foreground rounded-sm grid place-items-center font-display font-semibold">{i+1}</div>
                    <div className="text-sm sm:text-base leading-relaxed pt-1">{step}</div>
                  </li>
                ))}
              </ol>
              <OpenGuideLink track="pro" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-secondary transition-colors group">
                <Compass size={16} weight="bold"/> Voir le guide pas à pas (de A à Z)
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform"/>
              </OpenGuideLink>
            </div>
          </div>
        </div>
      </section>

      {/* Value pitch for partners */}
      <section className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-uppercase mb-4">{t.home.partner_kicker}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">{t.home.partner_h2}</h2>
            <p className="mt-4 text-muted-foreground">{t.home.partner_lead}</p>
            <Link to="/passez-partenaire" onClick={trackHeroCtaClick} className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-3 text-sm font-semibold rounded-sm transition-colors" data-testid="value-partner-cta">
              {t.home.cta_partner} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <Feature icon={<Lightning weight="duotone" size={22} />} title="Visibilité prioritaire" desc="Mise en avant dans la recherche IA et les pages de catégories." />
            <Feature icon={<ChartLine weight="duotone" size={22} />} title="Analytics simples" desc="Vues, clics téléphone & WhatsApp, demandes — tout est lisible." />
            <Feature icon={<ShieldCheck weight="duotone" size={22} />} title="Sans engagement" desc="Annulez en un clic, à tout moment, sans frais." />
            <Feature icon={<Globe weight="duotone" size={22} />} title="Frontalier inclus" desc="Visibilité optimisée pour le trafic suisse et allemand voisin." />
          </div>
        </div>
      </section>
      <SocialTicker />

      {/* Trust strip — RGPD compliance + made-in-Mulhouse signals */}
      <section className="border-t border-border bg-accent/20" data-testid="trust-strip">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            <TrustItem
              icon={<Lock size={18} weight="duotone"/>}
              title="100% RGPD"
              desc="Données hébergées en Europe. Conformité Article 17 et 20."
            />
            <TrustItem
              icon={<Download size={18} weight="duotone"/>}
              title="Vos données vous appartiennent"
              desc="Export complet en 1 clic depuis votre espace partenaire."
            />
            <TrustItem
              icon={<Heart size={18} weight="duotone"/>}
              title="Made in Mulhouse"
              desc="Une équipe locale, dans le Haut-Rhin, à votre écoute."
            />
            <TrustItem
              icon={<Clock size={18} weight="duotone"/>}
              title="14 jours gratuits"
              desc="Aucune carte demandée. Annulation en un clic, sans frais."
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function TrustItem({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3" data-testid="trust-item">
      <div className="w-9 h-9 shrink-0 bg-primary/10 text-primary rounded-sm grid place-items-center">{icon}</div>
      <div>
        <div className="text-sm font-display font-semibold leading-tight">{title}</div>
        <div className="text-xs text-muted-foreground leading-snug mt-1">{desc}</div>
      </div>
    </div>
  );
}

function Counter({ label, value, testid, accent = false }) {
  const [display, setDisplay] = useState(0);
  const [pop, setPop] = useState(false);
  const ref = React.useRef(null);
  const started = React.useRef(false);
  const prevValue = React.useRef(null);
  const isNumber = typeof value === "number" && Number.isFinite(value);

  useEffect(() => {
    if (!isNumber || started.current || !ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const target = value;
        const duration = 1400; // ms
        const start = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          setDisplay(Math.round(ease(progress) * target));
          if (progress < 1) requestAnimationFrame(tick);
          else prevValue.current = target;
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, isNumber]);

  // Live update: when value increases AFTER the intro animation finished,
  // trigger a brief "+1 pop" (terracotta flash + scale) so the visitor feels
  // the counter living in real time. No-op for non-numeric / unchanged values.
  useEffect(() => {
    if (!isNumber || !started.current) return;
    if (prevValue.current !== null && value > prevValue.current) {
      setDisplay(value);
      setPop(true);
      const t = setTimeout(() => setPop(false), 650);
      prevValue.current = value;
      return () => clearTimeout(t);
    }
    if (prevValue.current === null) prevValue.current = value;
    else if (value !== display) setDisplay(value);
  }, [value, isNumber, display]);

  const formatted = isNumber ? display.toLocaleString("fr-FR") : value;
  // Tailwind needs literal class names — branch instead of templating.
  const numberClass = pop
    ? "font-display text-3xl sm:text-4xl font-medium tabular-nums text-[#C8795A] scale-110 transition-all duration-300"
    : "font-display text-3xl sm:text-4xl font-medium tabular-nums transition-all duration-500";

  return (
    <div ref={ref} data-testid={testid} className="relative">
      <div className={numberClass}>{formatted}</div>
      {pop && (
        <span
          aria-hidden="true"
          className="absolute -top-2 left-12 sm:left-16 text-[#C8795A] text-xs font-bold tracking-wider pointer-events-none animate-pop-up"
          data-testid={`${testid}-pop`}
        >
          +1
        </span>
      )}
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function LiveHeroCount({ value }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const duration = 1600;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setN(Math.round(ease(p) * value));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value]);
  return <span>{n.toLocaleString("fr-FR")}+</span>;
}

/**
 * Compteur "recherches en direct" du hero.
 * - Rond rouge qui pulse (contraste vs le pill vert d'à côté).
 * - Total cumulé animé au montage + micro-tick +N à chaque refresh 25 s.
 * - Sous-titre "dont N cette semaine" — prouve que le trafic est RÉCENT
 *   (essentiel pour la preuve sociale pitchée par les télépros).
 */
function LiveSearchesPill({ total, last7d }) {
  const [display, setDisplay] = useState(0);
  const [flash, setFlash] = useState(false);
  const started = React.useRef(false);
  const prev = React.useRef(null);

  // Intro animation (count up from 0)
  useEffect(() => {
    if (started.current || typeof total !== "number") return;
    started.current = true;
    const start = performance.now();
    const duration = 1800;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setDisplay(Math.round(ease(p) * total));
      if (p < 1) requestAnimationFrame(tick);
      else prev.current = total;
    };
    requestAnimationFrame(tick);
  }, [total]);

  // Live tick: chaque poll qui incrémente déclenche un flash rouge.
  useEffect(() => {
    if (!started.current || typeof total !== "number") return;
    if (prev.current != null && total > prev.current) {
      setDisplay(total);
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 800);
      prev.current = total;
      return () => clearTimeout(t);
    }
  }, [total]);

  const numberClass = flash
    ? "font-bold tabular-nums text-[#B33A3A] transition-colors duration-300"
    : "font-bold tabular-nums transition-colors duration-500";

  return (
    <div
      className="inline-flex items-center gap-2 rounded-full pl-2 pr-3 py-1 border border-[#B33A3A]/25 bg-[#B33A3A]/8"
      data-testid="hero-live-searches"
      title="Nombre de recherches effectuées sur MonCommerce68 depuis le lancement"
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#B33A3A] opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#B33A3A]" />
      </span>
      <span className="text-[10px] uppercase tracking-widest font-bold text-[#B33A3A]">En direct</span>
      <span className={`text-xs ${numberClass}`} data-testid="hero-live-searches-total">
        {display.toLocaleString("fr-FR")}
      </span>
      <span className="text-xs text-muted-foreground">recherches</span>
      {typeof last7d === "number" && last7d > 0 && (
        <span className="hidden sm:inline text-[11px] text-muted-foreground" data-testid="hero-live-searches-7d">
          · dont <b className="text-foreground tabular-nums">{last7d.toLocaleString("fr-FR")}</b> cette semaine
        </span>
      )}
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="border border-border rounded-sm p-6 lift-card bg-background">
      <div className="text-primary mb-3">{icon}</div>
      <div className="font-display text-base font-semibold mb-1">{title}</div>
      <div className="text-sm text-muted-foreground leading-relaxed">{desc}</div>
    </div>
  );
}

function NearbyButton({ navigate }) {
  const [busy, setBusy] = useState(false);
  const click = () => {
    if (!navigator.geolocation) {
      toast.error("Votre navigateur ne supporte pas la géolocalisation");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        const { latitude, longitude } = pos.coords;
        navigate(`/autour-de-moi?lat=${latitude.toFixed(5)}&lon=${longitude.toFixed(5)}`);
      },
      (err) => {
        setBusy(false);
        // Code 1 = PERMISSION_DENIED (soit l'utilisateur a refusé, soit iframe sans `allow=geolocation`)
        // Code 2 = POSITION_UNAVAILABLE · Code 3 = TIMEOUT
        if (err.code === 1) {
          toast.error("Géolocalisation bloquée. En preview iframe c'est normal — ouvrez le site dans un onglet ou utilisez la recherche par ville.", { duration: 6000 });
        } else if (err.code === 3) {
          toast.error("Délai de localisation dépassé — vérifiez que le GPS est actif.");
        } else {
          toast.error("Position indisponible — vous pouvez chercher par ville.");
        }
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  };
  return (
    <button
      onClick={click}
      disabled={busy}
      className="inline-flex items-center gap-2 text-sm font-semibold border border-border bg-card hover:border-primary px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
      data-testid="cta-nearby"
    >
      <Crosshair size={14} weight="bold" className="text-primary"/> {busy ? "Localisation…" : "Autour de moi"}
    </button>
  );
}
